import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, RefreshCw, Check, X, Eye, Edit2, Trash2, 
  Image, Send, ExternalLink, ChevronDown, ChevronUp, Sparkles, Upload, Copy, RotateCcw, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

const DraftsManager = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [editObservation, setEditObservation] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [aiProgress, setAiProgress] = useState(0);
  
  // Regenerate article state
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const [regenerateTarget, setRegenerateTarget] = useState(null);
  const [regenerationError, setRegenerationError] = useState(null);
  
  // NEW: Tweet suggestion state
  const [showTweetDialog, setShowTweetDialog] = useState(false);
  const [tweetSuggestion, setTweetSuggestion] = useState('');
  const [publishedDossierId, setPublishedDossierId] = useState(null);
  const [publishingTweet, setPublishingTweet] = useState(false);
  const [tweetImprovePrompt, setTweetImprovePrompt] = useState('');
  const [improvingTweet, setImprovingTweet] = useState(false);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await api.getDraftDossiers();
      setDrafts(res.drafts || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handlePublish = async (dossierId) => {
    setProcessing(dossierId);
    try {
      const result = await api.publishDossier(dossierId);
      
      // Remove from drafts list immediately (optimistic update)
      setDrafts(prev => prev.filter(d => d.dossier_id !== dossierId));
      setSelectedDraft(null);
      
      // Show tweet suggestion dialog
      if (result.tweet_suggestion) {
        setTweetSuggestion(result.tweet_suggestion);
        setPublishedDossierId(dossierId);
        setShowTweetDialog(true);
      } else {
        alert('✅ Artículo publicado correctamente');
      }
      
      // Refresh in background to sync
      fetchDrafts();
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
      // Restore list on error
      fetchDrafts();
    } finally {
      setProcessing(null);
    }
  };

  const handlePublishTweet = async () => {
    if (!publishedDossierId || !tweetSuggestion.trim()) return;
    
    setPublishingTweet(true);
    try {
      const result = await api.publishTweetForDossier(publishedDossierId, tweetSuggestion);
      
      alert(`✅ Tweet publicado\n\n${result.tweet_url || 'Publicado correctamente'}`);
      setShowTweetDialog(false);
      setTweetSuggestion('');
      setTweetImprovePrompt('');
      setPublishedDossierId(null);
    } catch (error) {
      console.error('Error publishing tweet:', error);
      alert('❌ Error al publicar tweet: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishingTweet(false);
    }
  };

  const handleImproveTweet = async () => {
    if (!publishedDossierId || !tweetImprovePrompt.trim()) return;
    
    setImprovingTweet(true);
    try {
      const result = await api.improveTweetForDossier(
        publishedDossierId,
        tweetSuggestion,
        tweetImprovePrompt
      );
      
      if (result.improved_tweet) {
        setTweetSuggestion(result.improved_tweet);
        setTweetImprovePrompt(''); // Clear the instructions after success
      }
    } catch (error) {
      console.error('Error improving tweet:', error);
      alert('❌ Error al mejorar tweet: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImprovingTweet(false);
    }
  };

  const handleDelete = async (dossierId) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    setProcessing(dossierId);
    
    try {
      await api.deleteDossier(dossierId);
      
      // Remove from drafts list immediately (optimistic update)
      setDrafts(prev => prev.filter(d => d.dossier_id !== dossierId));
      setSelectedDraft(null);
      
      // Refresh in background to sync
      fetchDrafts();
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
      // Restore list on error
      fetchDrafts();
    } finally {
      setProcessing(null);
    }
  };

  const handleRegenerateImage = async (dossierId, customPrompt = null, referenceImages = []) => {
    setProcessing(dossierId + '_img');
    try {
      let res;
      if (referenceImages && referenceImages.length > 0) {
        res = await api.regenerateDossierImageWithRefs(dossierId, customPrompt || null, referenceImages);
      } else {
        res = await api.regenerateDossierImage(dossierId, customPrompt || null);
      }
      
      if (res.success) {
        // Update image immediately in UI
        if (selectedDraft?.dossier_id === dossierId) {
          setSelectedDraft(prev => ({ ...prev, cover_image: res.cover_image }));
        }
        
        setImagePrompt('');
        alert('✅ Imagen regenerada correctamente');
        
        // Refresh list in background to sync
        fetchDrafts();
      }
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleEditWithAI = async (dossierId) => {
    if (!editObservation.trim()) {
      alert('Escribe una observación para la IA');
      return;
    }
    
    // Set processing state
    setProcessing(dossierId + '_edit');
    setAiProgress(0);
    
    try {
      const res = await api.editDossierWithAI(dossierId, editObservation, (progress, status) => {
        // Update progress while processing
        setAiProgress(progress);
      });
      
      if (res.success) {
        // Clear the input immediately for better UX
        setEditObservation('');
        setAiProgress(100);
        
        // Show non-blocking success message
        alert('✅ Artículo editado correctamente. Refrescando...');
        
        // Fetch updates in parallel
        const [_, updated] = await Promise.all([
          fetchDrafts(),
          api.getDossier(dossierId)
        ]);
        
        setSelectedDraft(updated);
      }
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
      setAiProgress(0);
    }
  };

  const handleGetSuggestions = async (dossierId) => {
    // Set processing state but DON'T block UI
    setProcessing(dossierId + '_suggestions');
    setShowSuggestions(true);
    setAiProgress(0);
    
    try {
      const res = await api.getArticleSuggestions(dossierId, (progress, status) => {
        // Update progress indicator
        setAiProgress(progress);
      });
      if (res.suggestions) {
        setSuggestions(res.suggestions);
        // Insert the generated prompt directly into the edit field
        if (res.suggestions[0] && res.suggestions[0].prompt) {
          setEditObservation(res.suggestions[0].prompt);
        }
      }
    } catch (error) {
      alert('⚠️ Error obteniendo sugerencias: ' + (error.response?.data?.detail || error.message));
      setShowSuggestions(false);
    } finally {
      setProcessing(null);
      setAiProgress(0);
    }
  };

  // Regenerate article with optional instructions
  const handleRegenerateArticle = async () => {
    if (!regenerateTarget) return;
    
    setProcessing(regenerateTarget + '_regenerate');
    setRegenerationError(null);
    setAiProgress(0);
    
    try {
      const res = await api.regenerateArticle(
        regenerateTarget, 
        regenerateInstructions,
        (progress, status) => {
          setAiProgress(progress);
        }
      );
      
      if (res.success) {
        alert(`✅ Artículo regenerado exitosamente\n\nTítulo: ${res.result.title}\nPárrafos: ${res.result.paragraphs}\nFuentes: ${res.result.sources}`);
        setShowRegenerateDialog(false);
        setRegenerateInstructions('');
        setRegenerateTarget(null);
        fetchDrafts(); // Refresh list
      } else {
        // Show rejection details
        setRegenerationError({
          message: res.error || 'No se pudo regenerar el artículo',
          details: res.result
        });
      }
    } catch (error) {
      setRegenerationError({
        message: error.response?.data?.detail || error.message || 'Error desconocido',
        details: null
      });
    } finally {
      setProcessing(null);
      setAiProgress(0);
    }
  };

  // Open regenerate dialog
  const openRegenerateDialog = (dossierId) => {
    setRegenerateTarget(dossierId);
    setRegenerateInstructions('');
    setRegenerationError(null);
    setShowRegenerateDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Helper to check if article has rejection/error content
  const isRejectedArticle = (draft) => {
    const content = draft.content || '';
    return content.includes('fuentes consultadas no proporcionan suficiente') || 
           content.includes('no cumple los criterios') ||
           content.includes('Fuentes insuficientes') ||
           (draft.sources && draft.sources.length === 0 && content.length < 500);
  };

  // Helper to check if article needs review (passed after 3 rejections)
  const needsReview = (draft) => {
    return draft.needs_review === true;
  };

  // Get AI suggestion for improvement
  const getAISuggestion = (draft) => {
    return draft.ai_suggestion || draft.review_reason || null;
  };

  return (
    <div className="space-y-4">
      {/* Regenerate Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Rehacer Artículo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              El artículo será regenerado completamente buscando nuevas fuentes. 
              Puedes agregar instrucciones específicas para la IA.
            </p>
            
            <Textarea
              placeholder="Instrucciones para la IA (opcional)&#10;&#10;Ejemplos:&#10;- Enfocarse en el impacto económico en Guatemala&#10;- Incluir más datos sobre deportaciones&#10;- Buscar declaraciones oficiales del gobierno"
              value={regenerateInstructions}
              onChange={(e) => setRegenerateInstructions(e.target.value)}
              rows={5}
              className="resize-none"
            />
            
            {regenerationError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">{regenerationError.message}</p>
                    {regenerationError.details && (
                      <div className="mt-2 text-sm text-red-700">
                        {regenerationError.details.razon && (
                          <p><strong>Razón:</strong> {regenerationError.details.razon}</p>
                        )}
                        {regenerationError.details.datos_faltantes && (
                          <div className="mt-1">
                            <strong>Datos faltantes:</strong>
                            <ul className="list-disc list-inside mt-1">
                              {regenerationError.details.datos_faltantes.map((d, i) => (
                                <li key={i}>{d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {regenerationError.details.busquedas_ejecutadas && (
                          <p className="mt-1"><strong>Búsquedas ejecutadas:</strong> {regenerationError.details.busquedas_ejecutadas}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {processing === regenerateTarget + '_regenerate' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Regenerando artículo...</span>
                  <span className="font-medium">{aiProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${aiProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRegenerateDialog(false)}
              disabled={processing === regenerateTarget + '_regenerate'}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleRegenerateArticle}
              disabled={processing === regenerateTarget + '_regenerate'}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processing === regenerateTarget + '_regenerate' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Regenerando...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Rehacer Artículo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Artículos Pendientes de Revisión
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {drafts.length} artículos esperando aprobación
          </p>
        </div>
        <Button onClick={fetchDrafts} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay artículos pendientes de revisión</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((draft) => (
            <DraftCard
              key={draft.dossier_id}
              draft={draft}
              onView={() => setSelectedDraft(draft)}
              onPublish={() => handlePublish(draft.dossier_id)}
              onDelete={() => handleDelete(draft.dossier_id)}
              processing={processing === draft.dossier_id}
            />
          ))}
        </div>
      )}

      {/* Draft Preview Modal */}
      {selectedDraft && (
        <DraftPreviewModal
          draft={selectedDraft}
          onClose={() => { setSelectedDraft(null); setEditObservation(''); setImagePrompt(''); setShowSuggestions(false); setSuggestions([]); }}
          onPublish={() => handlePublish(selectedDraft.dossier_id)}
          onDelete={() => handleDelete(selectedDraft.dossier_id)}
          onRegenerateImage={(customPrompt) => handleRegenerateImage(selectedDraft.dossier_id, customPrompt)}
          onEditWithAI={() => handleEditWithAI(selectedDraft.dossier_id)}
          onGetSuggestions={() => handleGetSuggestions(selectedDraft.dossier_id)}
          onRegenerate={() => openRegenerateDialog(selectedDraft.dossier_id)}
          isRejected={isRejectedArticle(selectedDraft)}
          needsReview={needsReview(selectedDraft)}
          aiSuggestion={getAISuggestion(selectedDraft)}
          editObservation={editObservation}
          setEditObservation={setEditObservation}
          imagePrompt={imagePrompt}
          setImagePrompt={setImagePrompt}
          processing={processing}
          showSuggestions={showSuggestions}
          suggestions={suggestions}
        />
      )}
      
      {/* Tweet Suggestion Dialog */}
      {showTweetDialog && (
        <Dialog open={showTweetDialog} onOpenChange={setShowTweetDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Send className="w-6 h-6 text-blue-500" />
                Publicar Tweet
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-5">
              {/* Success message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Artículo publicado correctamente. Revisa y publica el tweet:
                </p>
              </div>
              
              {/* Tweet Preview with enhanced styling */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Vista previa del Tweet</label>
                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 space-y-3">
                  {/* Tweet headline - extracted and styled */}
                  {(() => {
                    const lines = tweetSuggestion.split('\n').filter(l => l.trim());
                    const headline = lines[0] || '';
                    const rest = lines.slice(1).join('\n');
                    return (
                      <>
                        <p className="text-lg font-bold text-gray-900 leading-tight">
                          {headline}
                        </p>
                        {rest && (
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">
                            {rest}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Editable tweet */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Editar Tweet</label>
                <Textarea
                  value={tweetSuggestion}
                  onChange={(e) => setTweetSuggestion(e.target.value)}
                  rows={4}
                  className="w-full p-3 border-2 rounded-lg text-gray-900 bg-white focus:border-blue-500"
                  placeholder="Edita el tweet sugerido..."
                />
                <div className="flex justify-between items-center">
                  <p className={`text-xs ${tweetSuggestion.length > 280 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                    {tweetSuggestion.length}/280 caracteres
                  </p>
                  {tweetSuggestion.length > 280 && (
                    <p className="text-xs text-red-500">⚠️ Tweet demasiado largo</p>
                  )}
                </div>
              </div>

              {/* AI Improvement Section */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <label className="text-sm font-semibold text-purple-700">Mejorar con IA</label>
                </div>
                <Textarea
                  value={tweetImprovePrompt}
                  onChange={(e) => setTweetImprovePrompt(e.target.value)}
                  rows={2}
                  className="w-full p-3 border border-purple-200 rounded-lg text-gray-900 bg-white focus:border-purple-500 text-sm"
                  placeholder="Ej: Hazlo más viral, agrega urgencia, usa emojis de tecnología, cambia el tono a más profesional..."
                  disabled={improvingTweet}
                />
                <Button
                  onClick={handleImproveTweet}
                  disabled={improvingTweet || !tweetImprovePrompt.trim()}
                  variant="outline"
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  {improvingTweet ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Mejorando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Aplicar Mejora con IA
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                onClick={() => {
                  setShowTweetDialog(false);
                  setTweetSuggestion('');
                  setTweetImprovePrompt('');
                  setPublishedDossierId(null);
                }}
                variant="outline"
                disabled={publishingTweet || improvingTweet}
              >
                Cancelar
              </Button>
              <Button
                onClick={handlePublishTweet}
                disabled={publishingTweet || improvingTweet || !tweetSuggestion.trim() || tweetSuggestion.length > 280}
                className="bg-black hover:bg-gray-800"
              >
                {publishingTweet ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Publicando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Publicar Tweet
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const DraftCard = ({ draft, onView, onPublish, onDelete, processing }) => (
  <div className="bg-white rounded-xl border p-4">
    <div className="flex gap-4">
      {/* Image preview */}
      <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
        {draft.cover_image ? (
          <img src={draft.cover_image?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${draft.cover_image}` : draft.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-8 h-8 text-gray-300" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge className="bg-yellow-100 text-yellow-700">Borrador</Badge>
          <Badge variant="outline">{draft.category || draft.sector}</Badge>
        </div>
        <h3 className="font-semibold line-clamp-2 mb-1">{draft.title}</h3>
        <p className="text-sm text-gray-500 line-clamp-1">
          {draft.executive_summary?.[0] || draft.content?.slice(0, 100)}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Creado: {new Date(draft.created_at).toLocaleString('es-MX')}
        </p>
      </div>
      
      <div className="flex flex-col gap-2">
        <Button variant="outline" size="sm" onClick={onView}>
          <Eye className="w-4 h-4" />
        </Button>
        <Button size="sm" className="bg-green-600" onClick={onPublish} disabled={processing}>
          {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
        <Button variant="outline" size="sm" className="text-[#1e3a5f]" onClick={onDelete}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);

const DraftPreviewModal = ({ 
  draft, onClose, onPublish, onDelete, onRegenerateImage, 
  onEditWithAI, onGetSuggestions, onRegenerate, isRejected, needsReview, aiSuggestion,
  editObservation, setEditObservation, 
  imagePrompt, setImagePrompt, processing, showSuggestions, suggestions 
}) => {
  const [showSources, setShowSources] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);
  const fileInputRef = useRef(null);
  
  const handleSuggestPrompt = async () => {
    setLoadingSuggestion(true);
    try {
      const res = await api.suggestImagePrompt(draft.dossier_id);
      if (res.success && res.suggestion) {
        setImagePrompt(res.suggestion);
      }
    } catch (error) {
      console.error('Error getting suggestion:', error);
    } finally {
      setLoadingSuggestion(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    setReferenceImages(prev => [...prev, ...imageFiles].slice(0, 3)); // Max 3 images
  };
  
  const removeRefImage = (index) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vista Previa del Artículo</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Cover Image */}
          <div className="relative">
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              {draft.cover_image ? (
                <img src={draft.cover_image?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${draft.cover_image}` : draft.cover_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
          </div>
          
          {/* Image Regeneration Controls - Now as a separate toolbar */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg border">
              {showImageOptions && (
                <div className="bg-white rounded-lg shadow-lg p-3 w-96">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">
                      Instrucciones para la imagen (opcional):
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      onClick={handleSuggestPrompt}
                      disabled={loadingSuggestion}
                    >
                      {loadingSuggestion ? (
                        <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      Sugerir
                    </Button>
                  </div>
                  <Textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Ej: Una imagen de un político dando un discurso en un congreso, tonos azules..."
                    rows={2}
                    className="text-sm mb-2"
                  />
                  
                  {/* Reference Images Upload */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-500">Imágenes de referencia (opcional):</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        Adjuntar
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>
                    {referenceImages.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {referenceImages.map((file, idx) => (
                          <div key={idx} className="relative w-16 h-16 rounded overflow-hidden border">
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                            <button
                              onClick={() => removeRefImage(idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {referenceImages.length === 0 && (
                      <p className="text-[10px] text-gray-400">Máx. 3 imágenes para usar como referencia visual</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      onClick={() => {
                        onRegenerateImage(imagePrompt, referenceImages);
                        setShowImageOptions(false);
                        setReferenceImages([]);
                      }}
                      disabled={processing === draft.dossier_id + '_img'}
                    >
                      {processing === draft.dossier_id + '_img' ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Image className="w-4 h-4 mr-1" />
                      )}
                      Generar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setShowImageOptions(false);
                        setReferenceImages([]);
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
              <Button 
                size="sm" 
                variant="outline"
                className="bg-white"
                onClick={() => setShowImageOptions(!showImageOptions)}
                disabled={processing === draft.dossier_id + '_img'}
              >
                {processing === draft.dossier_id + '_img' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Image className="w-4 h-4 mr-2" />
                )}
                Regenerar Imagen
              </Button>
              
              {/* Suggestions Button */}
              <Button 
                size="sm"
                variant="outline"
                className="bg-white border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={onGetSuggestions}
                disabled={processing === draft.dossier_id + '_suggestions'}
              >
                {processing === draft.dossier_id + '_suggestions' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Sugerencias IA
              </Button>
              
              {/* Regenerate Article Button */}
              <Button 
                size="sm"
                variant="outline"
                className="bg-white border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={onRegenerate}
                disabled={processing?.includes(draft.dossier_id)}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Rehacer Artículo
              </Button>
            </div>
          
          {/* Rejection Warning */}
          {isRejected && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800">Artículo con problemas</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Este artículo fue rechazado por falta de fuentes o no cumplir los criterios editoriales.
                    Usa el botón "Rehacer Artículo" para intentar regenerarlo con nuevas búsquedas.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Needs Review Warning - Article passed after 3 rejections */}
          {needsReview && !isRejected && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-800">Artículo necesita revisión</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Este artículo fue aprobado automáticamente después de múltiples intentos de mejora.
                    Revísalo antes de publicar o usa "Rehacer Artículo" con las siguientes instrucciones:
                  </p>
                  {aiSuggestion && (
                    <div className="mt-2 p-2 bg-blue-100 rounded text-sm text-blue-900">
                      <strong>💡 Sugerencia IA:</strong> {aiSuggestion}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Title & Meta */}
          <div>
            <Badge className="mb-2">{draft.category || draft.sector}</Badge>
            {isRejected && (
              <Badge className="mb-2 ml-2 bg-amber-100 text-amber-800 border-amber-300">⚠️ Rechazado</Badge>
            )}
            {needsReview && !isRejected && (
              <Badge className="mb-2 ml-2 bg-blue-100 text-blue-800 border-blue-300">🔍 Revisar</Badge>
            )}
            <h1 className="text-2xl font-bold">{draft.title}</h1>
          </div>
          
          {/* Content Preview */}
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 font-medium border-l-4 border-red-600 pl-4">
              {draft.executive_summary?.[0] || draft.content?.replace(/<[^>]+>/g, '').replace(/```html|```/g, '').slice(0, 200)}
            </p>
            
            <div 
              className="mt-6 space-y-4 text-gray-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ 
                __html: (draft.content?.includes('<p>') 
                  ? draft.content 
                  : draft.content?.split('\n\n').map(p => `<p>${p.trim()}</p>`).join('') || ''
                ).replace(/```html|```/g, '')
              }}
            />
          </div>
          
          {/* Sources */}
          <div className="border-t pt-4 space-y-4">
            <button 
              onClick={() => setShowSources(!showSources)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {showSources ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Fuentes Consultadas ({(draft.sources?.length || 0)})
            </button>
            
            {showSources && (
              <div className="space-y-4">
                {/* Search Query Used */}
                {draft.search_query && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-1">Búsqueda realizada:</p>
                    <p className="text-sm text-gray-700 italic">"{draft.search_query}"</p>
                  </div>
                )}
                
                {/* Firecrawl Sources */}
                {draft.sources?.length > 0 && (
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-orange-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">F</span>
                      </div>
                      <h5 className="font-semibold text-orange-800">Firecrawl ({draft.sources.length})</h5>
                      <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Contexto Web</span>
                    </div>
                    <ul className="space-y-1">
                      {draft.sources.map((src, i) => (
                        <li key={i} className="text-sm">
                          <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-orange-700 hover:underline flex items-start gap-1">
                            <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0" />
                            <span>{src.title || src.url}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* No sources message */}
                {!draft.sources?.length && (
                  <p className="text-sm text-gray-500 italic">No se encontraron fuentes externas para este artículo.</p>
                )}
              </div>
            )}
          </div>
          
          {/* Edit with AI */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Editar con IA</h4>
            <p className="text-sm text-gray-500 mb-2">
              Escribe una observación y la IA editará el artículo según tus indicaciones.
            </p>
            <Textarea
              value={editObservation}
              onChange={(e) => setEditObservation(e.target.value)}
              placeholder="Ej: Agrega más contexto sobre el impacto económico, hazlo más crítico con el gobierno..."
              rows={3}
            />
            <div className="flex gap-2 mt-2">
              <Button 
                onClick={onEditWithAI}
                disabled={!editObservation.trim() || processing === draft.dossier_id + '_edit'}
              >
                {processing === draft.dossier_id + '_edit' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Edit2 className="w-4 h-4 mr-2" />
                )}
                Aplicar Edición
              </Button>
              
              <Button 
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={onGetSuggestions}
                disabled={processing === draft.dossier_id + '_suggestions'}
              >
                {processing === draft.dossier_id + '_suggestions' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Sugerencias IA
              </Button>
            </div>
          </div>
          
          {/* AI Suggestions */}
          {showSuggestions && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Recomendaciones de IA
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                El prompt se ha insertado automáticamente arriba. Revisa las recomendaciones y haz clic en "Aplicar Edición".
              </p>
              {suggestions.length > 0 ? (
                <div className="space-y-3">
                  {suggestions.map((suggestion, idx) => (
                    <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="bg-purple-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-purple-900">{suggestion.title}</p>
                          <p className="text-sm text-gray-700 mt-1">{suggestion.description}</p>
                          {suggestion.example && (
                            <p className="text-xs text-gray-500 italic mt-1">Ej: {suggestion.example}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-purple-600 animate-spin" />
                  <span className="ml-2 text-gray-600">Generando sugerencias...</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button variant="outline" className="text-[#1e3a5f]" onClick={onDelete}>
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar
          </Button>
          <Button className="bg-green-600" onClick={onPublish} disabled={processing === draft.dossier_id}>
            {processing === draft.dossier_id ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Publicar Artículo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DraftsManager;
